import { SubmissionError, reset, getFormValues, change } from 'redux-form';
import { handleActions } from 'redux-actions';
import { LOCATION_CHANGE } from 'react-router-redux';
import cloneDeep from 'lodash/cloneDeep';
import pick from 'lodash/pick';
import forEach from 'lodash/forEach';
import flatten from 'lodash/flatten';
import _get from 'lodash/get';
import copy from 'copy-to-clipboard';

import moment from 'moment';
import http from '../../shared/http';
import endpoints from '../../../config/endpoints';
import intl from '../../shared/intl';
import checkPermissions from '../../shared/check-permissions';
import createFeatureFlagsForm from '../components/form/feature-flags.index';

import {
	createSelectOptionsFromSysEnums,
	createSelectValuesUseIntersection
} from '../../shared/form-elements/select';

const featureFlagsForm = createFeatureFlagsForm();

// permissions
export const allowedPermissions = {
	'view': 'affiliates_list',
	'edit_feature_flags': 'affiliates_update_feature_flags',
	'add_lead_source': 'add_lead_source',
	'edit': 'affiliates_create'
};

// actions types
const TOGGLE_PROGRESS_BAR = 'affiliateEdit/toggle-progress-bar';

const FETCH_DATA_SUCCESS = 'affiliateEdit/fetch-data-affiliates-success';
const FETCH_DATA_ERROR = 'affiliateEdit/fetch-data-affiliates-error';

const FETCH_LOCATIONS_SUCCESS = 'affiliateEdit/fetch-locations-success';
const FETCH_LOCATIONS_ERROR = 'affiliateEdit/fetch-locations-error';

const FETCH_LEAD_SUCCESS = 'affiliateEdit/FETCH_LEAD_SUCCESS';

const ADD_NEW_LEAD_SOURCE = 'affiliateEdit/ADD_NEW_LEAD_SOURCE';

const FETCH_CITIES_SUCCESS = 'affiliateEdit/fetch-cities-success';
const FETCH_CITIES_ERROR = 'affiliateEdit/fetch-cities-error';

const FETCH_ADMINS_SUCCESS = 'affiliateEdit/fetch-admins-success';
const FETCH_ADMINS_ERROR = 'affiliateEdit/fetch-admins-error';

const SUBMIT_FORM_SUCCESS = 'affiliateEdit/submit-form-success';
const SUBMIT_FORM_ERROR = 'affiliateEdit/submit-form-error';

const CHANGE_TAB = 'affiliateEdit/change-tab';

const FETCH_OFFERS_SUCCESS = 'affiliateEdit/fetch-offers-success';

const GET_CONFIRMATION_LINK = 'affiliateEdit/get-confirmation-link';
const SEND_CONFIRMATION_EMAIL = 'affiliateEdit/send-confirmation-email';

const RESET_DATA = 'affiliateEdit/RESET_DATA';

// initial state
const initialState = {
	data: null,
	showProgressBar: false,
	individualTerms: null,
	offers: null,
	currentTab: 'main',
	forms: {
		main: {
			email: '',
			nickname: '',
			phone: '',
			skype: '',
			whatsapp: '',
			telegram: '',
			level: null,
			status: null,
			manager_regions: null,
			internal_rank: null,
			language: null,
			timezone: null,
			location_id: null,
			city_id: null,
			manager_id: null,
			tags: '',
			lead_source_names: null,
			whitelisted_ips: null,
			birthday: '',
			ref_id: null,
			is_master: false,
			allow_dangerous_snippets: false,
			is_need_birthday_notification: false,
			is_suspicious: false,
			is_possible_fraud: false,
			is_pseudotrash_enabled: false,
			is_staff: false,
			is_top: false
		},
		credentials: {
			password: ''
		},
		feature_flags: featureFlagsForm.initialState
	},
	error: false
};

// actions
export const actions = Object.assign({
	fetchData: props => dispatch => {
		(http({
			url: `${endpoints.AFFILIATES.BASE}/${props.params.id}`,
			method: 'get',
			actions: [
				FETCH_DATA_SUCCESS,
				FETCH_DATA_ERROR
			]
		}))(dispatch);
	},
	locationsLookup1: text => dispatch => {
		const request = http({
			url: `${endpoints.LOOKUPS.LOCATIONS}?query=${text}`,
			method: 'get',
			actions: [
				FETCH_LOCATIONS_SUCCESS,
				FETCH_LOCATIONS_ERROR
			]
		});

		return request(dispatch).then(action => {
			if (action.type === FETCH_LOCATIONS_SUCCESS) {
				return {
					options: createSelectOptionsFromSysEnums(action.payload.data)
				};
			}
		});
	},
	loadRegionsLookup: (text, isShowAll = false) => dispatch => {
		const request = http({
			url: endpoints.LOOKUPS.REGIONS,
			method: 'get',
			actions: [
				FETCH_LOCATIONS_SUCCESS,
				FETCH_LOCATIONS_ERROR
			]
		});

		return request(dispatch).then(action => {
			if (action.type === FETCH_LOCATIONS_SUCCESS) {
				return {
					options: createSelectOptionsFromSysEnums(action.payload.data)
						.filter(item => item.label)
				};
			}
		});
	},

	locationsLookup: text => dispatch => {
		const request = http({
			url: `${endpoints.LOOKUPS.LOCATIONS}?query=${text}`,
			method: 'get',
			actions: [
				FETCH_LOCATIONS_SUCCESS,
				FETCH_LOCATIONS_ERROR
			]
		});

		return request(dispatch).then(action => {
			if (action.type === FETCH_LOCATIONS_SUCCESS) {
				return {
					options: createSelectOptionsFromSysEnums(action.payload.data)
				};
			}
		});
	},
	leadSourcesLookup: text => (dispatch, getState) => {
		const canAddLeadSource = selectors.getCan(getState())('add_lead_source');
		const request = http({
			url: `${endpoints.LOOKUPS.LEAD_SOURCES}?query=${text}`,
			method: 'get',
			actions: [ FETCH_LEAD_SUCCESS ]
		});

		return request(dispatch).then(action => {
			if (action.type === FETCH_LEAD_SUCCESS) {
				if (!action.payload.data.length && canAddLeadSource) {
					return { options: [{ label: text, value: text, isNew: true }]};
				}
				return { options: createSelectOptionsFromSysEnums( action.payload.data) };
			}
		});
	},

	citiesLookup: search => dispatch => {
		const request = http({
			url: endpoints.LOOKUPS.CITIES,
			method: 'get',
			actions: [
				FETCH_CITIES_SUCCESS,
				FETCH_CITIES_ERROR
			],
			query: { query: search }
		});

		return request(dispatch).then((action) => {
			if (action.type === FETCH_CITIES_SUCCESS) {
				return {
					options: createSelectOptionsFromSysEnums(action.payload.data)
				};
			}
		});
	},

	managersLookup: text => dispatch => {
		const request = http({
			url: `${endpoints.LOOKUPS.ADMINS}?query=${text}`,
			method: 'get',
			actions: [
				FETCH_ADMINS_SUCCESS,
				FETCH_ADMINS_ERROR
			]
		});

		return request(dispatch).then(action => {
			if (action.type === FETCH_ADMINS_SUCCESS) {
				return {
					options: createSelectOptionsFromSysEnums(action.payload.data)
				};
			}
		});
	},

	updatePossibleRegions: (index) => (dispatch, getState) => {
		const formValues = getFormValues('affiliateMainForm')(getState());
		const admin_id = _get(formValues, `additional_managers[${index}].manager[0].value`);
		const request = http({
			url: `${endpoints.LOOKUPS.REGIONS}?admin_id=${admin_id}`,
			method: 'get',
			actions: [
				FETCH_ADMINS_SUCCESS, FETCH_ADMINS_ERROR
			]
		});

		return request(dispatch).then(action => {
			if (action.type === FETCH_ADMINS_SUCCESS) {
				dispatch(change(
					'affiliateMainForm',
					`additional_managers[${index}].regions`,
					createSelectOptionsFromSysEnums(action.payload.data)
				));
			}
		});
	},

	submitForm: formData => (dispatch, getState) => {
		const { data: { id, is_master }, forms, currentTab } = getState().affiliateEdit;

		let requestData = null;

		const birthdays = formData.birthday && formData.birthday
			.split(',')
			.filter(bd => bd)
			.filter(bd => moment(bd).isValid() || moment(`1900-${bd}`).isValid())
			.map(bd => moment(`1900-${bd}`).isValid() ? `1900-${bd}` : bd)
			.map(bd => moment(bd).format('YYYY-MM-DD'));

		let [ main_birthday, ...additional_birthdays ] = birthdays || [];

		if (formData.birthday && !main_birthday) {
			alert('Invalid birthday date format. Valid example: 1999-01-01');
			main_birthday = null;
		}

		if (formData.tags && formData.tags.length) {
			formData.tags = flatten(formData.tags.map(val => val.split(',')));
		}
		if (formData.whitelisted_ips && formData.whitelisted_ips.length) {
			formData.whitelisted_ips = flatten(formData.whitelisted_ips.map(val => val.split(',')));
		}

		if (currentTab === 'main' && is_master && !formData.is_master) {
			let confirm = prompt(
				`You are about to remove this affiliate's master status.\n\n` +
				`This will result in all child affiliates being removed from this master account ` +
				`and their offers and individual terms removed as well.\n\n` +
				`Please type 'yes' to confirm.`
			);

			if (confirm !== 'yes') return dispatch(reset('affiliateMainForm'));
		}

		if (currentTab === 'main') {
			requestData = pick({
				...forms.main,
				...formData,
				internal_rank: formData.internal_rank && formData.internal_rank[0] && formData.internal_rank[0].value || null,
				status: formData.status && formData.status[0] && formData.status[0].value || null,
				level: formData.level && formData.level[0] && formData.level[0].value || null,
				language: formData.language && formData.language[0] && formData.language[0].value || null,
				timezone: formData.timezone && formData.timezone[0] && formData.timezone[0].value || null,
				location_id: formData.location_id && formData.location_id[0] && formData.location_id[0].value || null,
				city_id: formData.city_id && formData.city_id[0] && formData.city_id[0].value || null,
				manager_id: formData.manager_id && formData.manager_id[0] && formData.manager_id[0].value || null,
				tags: formData.tags ? formData.tags.map(val => val.trim()).filter(val => val) : null,
				lead_source_names: formData.lead_source_names && formData.lead_source_names.length
					? formData.lead_source_names.map(val => val.value)
					: null,
				birthday: main_birthday,
				additional_birthdays: additional_birthdays.length ? additional_birthdays : null,
				is_master: !!formData.is_master,
				manager_regions: formData.manager_regions && formData.manager_regions.map(mr => mr.value),
				additional_managers: formData.additional_managers && formData.additional_managers
					.filter(mng => mng.regions && mng.regions.length)
					.reduce((acc, val) => [
						...acc, ...val.regions.map(rgn => ({ manager_id: _get(val, 'manager.0.value'), region: rgn.value}))
					], []),
				allow_dangerous_snippets: !!formData.allow_dangerous_snippets,
				is_need_birthday_notification: !!formData.is_need_birthday_notification,
				is_suspicious: !!formData.is_suspicious,
				is_possible_fraud: !!formData.is_possible_fraud,
				is_staff: !!formData.is_staff,
				is_pseudotrash_enabled: !!formData.is_pseudotrash_enabled,
				is_top: !!formData.is_top
			}, Object.keys(forms.main).concat([ 'additional_birthdays', 'additional_managers' ]));
		}

		if (currentTab === 'credentials') {
			requestData = {
				...forms.credentials,
				...formData
			};
		}

		dispatch(actions.toggleProgressBar());

		const request = http({
			url: `${endpoints.AFFILIATES.BASE}/${id}`,
			method: 'put',
			actions: [
				SUBMIT_FORM_SUCCESS,
				SUBMIT_FORM_ERROR
			],
			data: requestData
		});

		return request(dispatch)
			.then(action => {
				if (action.type === SUBMIT_FORM_SUCCESS) {
					dispatch(actions.toggleProgressBar());
					dispatch(actions.fetchData({
						params: { id }
					}));
				}

				if (action.type === SUBMIT_FORM_ERROR && action.payload.response.status === 400) {
					const validationsErrors = {};
					const responseErrors = action.payload.response.data;

					forEach(responseErrors, value => {
						const key = value.dataPath.slice(1);
						validationsErrors[key] = value.message;
					});

					throw new SubmissionError(validationsErrors);
				}
			});
	},

	toggleProgressBar: () => ({
		type: TOGGLE_PROGRESS_BAR
	}),

	changeTab: tabIndex => (dispatch, getState) => {
		const { data: { id }, individualTerms } = getState().affiliateEdit;

		if (tabIndex === 'individual-terms' && !individualTerms) {
			dispatch(actions.toggleProgressBar());
			dispatch(actions.fetchOffers(id));
		}

		dispatch({
			type: CHANGE_TAB,
			payload: tabIndex
		});
	},
	switchHungarianCampaigns: (currency) => (dispatch, getState) => {
		const affiliate_id = getState().affiliateEdit.data && getState().affiliateEdit.data.id;
		return (http({
			url: `${endpoints.CAMPAIGNS.BASE}/currency`,
			method: 'put',
			data: { country_code: 'HU', affiliate_id, currency },
			actions: [ RESET_DATA ]
		}))(dispatch);
	},

	fetchOffers: id => dispatch => {
		return (http({
			url: `${endpoints.OFFERS.BASE}?affiliate_id=${id}`,
			method: 'get',
			actions: [
				FETCH_OFFERS_SUCCESS,
				FETCH_DATA_ERROR
			]
		}))(dispatch);
	},

	copyConfirmationLink: (id) => dispatch => {
		dispatch(actions.toggleProgressBar());

		const request = http({
			url: endpoints.AFFILIATES.CONFIRMATION_LINK.replace('{id}', id),
			method: 'get',
			actions: [
				GET_CONFIRMATION_LINK,
				FETCH_DATA_ERROR
			]
		});

		return request(dispatch).then(action => {
			if (action.type === GET_CONFIRMATION_LINK) {
				copy(action.payload.data.link);

				dispatch(actions.toggleProgressBar());
			}
		});
	},

	sendConfirmationEmail: (email) => dispatch => {
		if (!confirm(`Send confirmation email to ${email} ?`)) return;

		dispatch(actions.toggleProgressBar());

		const request = http({
			url: endpoints.AFFILIATES.SEND_CONFIRMATION_EMAIL,
			method: 'post',
			actions: [
				SEND_CONFIRMATION_EMAIL,
				FETCH_DATA_ERROR
			],
			data: { email }
		});

		return request(dispatch).then(action => {
			if (action.type === SEND_CONFIRMATION_EMAIL) {
				dispatch(actions.toggleProgressBar());
			}
		});
	},

	addNewLeadSource: () => (dispatch, getState) => {
		dispatch(actions.toggleProgressBar());
		const affiliateMainForm = getFormValues('affiliateMainForm')(getState());
		const request = http({
			url: endpoints.AFFILIATES.ADD_NEW_LEAD_SOURCE,
			method: 'post',
			actions: [ ADD_NEW_LEAD_SOURCE ],
			data: { lead_source: _get(affiliateMainForm, 'lead_source_names.0.value') }
		});

		return request(dispatch).then(action => {
			if (action.type === ADD_NEW_LEAD_SOURCE) {
				dispatch(actions.toggleProgressBar());
			}
		});
	},

	timezonesLookup: text => (dispatch, getState) => {
		const timezones = (getState()).root.lists.timezones;
		const search = timezones.filter(item => text ? item.text.toLowerCase().indexOf(text.toLowerCase()) !== -1 : item);
		const options = createSelectOptionsFromSysEnums(search.slice(0, 4));
		return Promise.resolve({ options });
	}
},
featureFlagsForm.actions
);

// reducers
const reducers = Object.assign({
	[LOCATION_CHANGE]: (state, action) => {
		if (action.payload.pathname.indexOf('affiliates/edit') === -1) {
			return initialState;
		}
		return state;
	},
	[FETCH_DATA_SUCCESS]: (state, action) => ({
		...state,
		data: action.payload.data,
		showProgressBar: false,
		error: false
	}),
	[FETCH_DATA_ERROR]: state => ({ ...state, error: true, showProgressBar: false }),
	[RESET_DATA]: state => ({ ...state, data: null }),
	[TOGGLE_PROGRESS_BAR]: state => ({ ...state, showProgressBar: !state.showProgressBar }),
	[CHANGE_TAB]: (state, action) => ({ ...state, currentTab: action.payload}),
	[FETCH_OFFERS_SUCCESS]: (state, actions) => {
		const { data: { id } } = state;
		const offers  = actions.payload.data;
		const usersOffers = offers.filter(entity => entity.available);
		const usersOfferTerms = usersOffers.map(offer => {
			return offer.payouts.map(payout => ({
				...payout,
				affiliate_id: id,
				offer: offer.title,
				offer_id: offer.id,
				status: 'active'
			}));
		});

		return ({
			...state,
			offers: usersOffers,
			individualTerms: flatten(usersOfferTerms),
			showProgressBar: false
		});
	},
	[FETCH_LOCATIONS_ERROR]: errorReducerFn,
	[FETCH_CITIES_ERROR]: errorReducerFn,
	[FETCH_ADMINS_ERROR]: errorReducerFn,
	[SUBMIT_FORM_ERROR]: errorReducerFn
},
featureFlagsForm.reducers
);

export default handleActions(reducers, cloneDeep(initialState));

// selectors
export const selectors = {
	getData: state => state.affiliateEdit.data,
	getIsNewLeadSource: state => {
		const searchForm = getFormValues('affiliateMainForm')(state);

		return _get(searchForm, 'lead_source_names.0.isNew', false);
	},
	getIntl: state => intl(state),
	getProgressBar: state => state.affiliateEdit.showProgressBar,
	getCan: state => checkPermissions(state, allowedPermissions),
	getCurrentTab: state => state.affiliateEdit.currentTab,
	getFormData: state => {
		const { data, forms } = state.affiliateEdit;
		const statuses = createSelectOptionsFromSysEnums(state.root.lists['affiliate-statuses']);
		const levels = createSelectOptionsFromSysEnums(state.root.lists['affiliate-levels']);
		const languages = createSelectOptionsFromSysEnums(state.root.lists.languages);
		const timezones = createSelectOptionsFromSysEnums(state.root.lists.timezones);
		const internalRanks = createSelectOptionsFromSysEnums(state.root.lists['affiliate-internal-ranks']);
		const internalStatuses = createSelectOptionsFromSysEnums(state.root.lists['affiliate-internal-statuses']);

		const birthdays = data && data.birthday ? [ data.birthday ] : null;

		if (birthdays && data.additional_birthdays && data.additional_birthdays[0]) {
			birthdays.push(...data.additional_birthdays);
		}

		const initialValues = data && {
			...forms.main,
			...data,
			feature_flags: JSON.stringify(data.feature_flags),
			internal_rank: createSelectValuesUseIntersection(internalRanks, [data.internal_rank]),
			status: createSelectValuesUseIntersection(statuses, [data.status]),
			level: createSelectValuesUseIntersection(levels, [data.level]),
			language: createSelectValuesUseIntersection(languages, [data.language]),
			timezone: createSelectValuesUseIntersection(timezones, [data.timezone]),
			birthday: birthdays ? birthdays.join(',') : null,
			location_id: [{
				label: data.location,
				value: data.location_id
			}],
			additional_managers: data.additional_managers && data.additional_managers.map(man => ({
				manager: [{ label: man.manager_email, value: man.manager_id }],
				regions: man.regions && man.regions.map(rr => ({ label: rr, value: rr }))
			})),
			manager_regions: data.current_manager_regions &&
        data.current_manager_regions.map(mr => ({ label: mr, value: mr })),
			city_id: data.city ? [{
				label: data.city.text,
				value: data.city.id
			}] : null,
			manager_id: data.manager ? [{
				label: data.manager,
				value: data.manager_id
			}] : null,
			tags: data.tags || '',
			lead_source_names: data.lead_source_names
				? data.lead_source_names.map(item => ({value: item, label: item }))
				: null
		} || null;

		return {
			initialValues,
			options: {
				statuses,
				levels,
				languages,
				timezones,
				internalRanks,
				internalStatuses
			}
		};
	},
	// getIndividualTermsFormData: individualTermsForm.selectors.getFormData,
	getError: state => state.affiliateEdit.error,
	...featureFlagsForm.selectors
};

const errorReducerFn = (state, action) => {
	if (action.payload.response.status !== 400) {
		return ({ ...state, error: true, showProgressBar: false });
	}
	return state;
};



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/affiliates/edit/index.js